import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '../services/api';

export const useAnalyticsStore = defineStore('analytics', () => {
  // State
  const summary = ref(null);
  const applicationOverview = ref(null);
  const charts = ref(null);
  const dailyData = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const selectedPeriod = ref('day');
  const selectedApplicationId = ref(null);

  // Getters
  const isLoading = computed(() => loading.value);
  const errorMessage = computed(() => error.value);
  const currentPeriod = computed(() => selectedPeriod.value);

  // Actions
  const setLoading = (value) => {
    loading.value = value;
  };

  const setError = (errorMessage) => {
    error.value = errorMessage;
  };

  const clearError = () => {
    error.value = null;
  };

  /**
   * Получить общую сводку по всем приложениям
   */
  const fetchSummary = async (period = 'day') => {
    try {
      setLoading(true);
      clearError();
      
      const response = await api.get('/analytics/summary', {
        params: { period }
      });
      summary.value = response.data;
      selectedPeriod.value = period;
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при загрузке сводки');
      console.error('Error fetching analytics summary:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Получить обзор аналитики для конкретного приложения
   */
  const fetchApplicationOverview = async (applicationId, period = 'day') => {
    try {
      setLoading(true);
      clearError();
      
      const response = await api.get(`/analytics/applications/${applicationId}/overview`, {
        params: { period }
      });
      applicationOverview.value = response.data;
      selectedApplicationId.value = applicationId;
      selectedPeriod.value = period;
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при загрузке аналитики приложения');
      console.error('Error fetching application overview:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Получить данные для графиков
   */
  const fetchCharts = async (applicationId, period = 'day') => {
    try {
      setLoading(true);
      clearError();
      
      const response = await api.get(`/analytics/applications/${applicationId}/charts`, {
        params: { period }
      });
      charts.value = response.data;
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при загрузке графиков');
      console.error('Error fetching charts:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Получить ежедневную статистику
   */
  const fetchDailyData = async (applicationId, days = 30) => {
    try {
      setLoading(true);
      clearError();
      
      const response = await api.get(`/analytics/applications/${applicationId}/daily`, {
        params: { days }
      });
      dailyData.value = response.data;
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при загрузке ежедневной статистики');
      console.error('Error fetching daily data:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Получить realtime метрики
   */
  const fetchRealtime = async (applicationId) => {
    try {
      const response = await api.get(`/analytics/applications/${applicationId}/realtime`);
      return response.data;
    } catch (err) {
      console.error('Error fetching realtime metrics:', err);
      return null;
    }
  };

  /**
   * Экспорт метрик
   */
  const exportMetrics = async (applicationId, period = 'day') => {
    try {
      setLoading(true);
      
      const response = await api.get(`/analytics/applications/${applicationId}/export`, {
        params: { period }
      });
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при экспорте метрик');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Сбросить состояние
   */
  const reset = () => {
    summary.value = null;
    applicationOverview.value = null;
    charts.value = null;
    dailyData.value = null;
    error.value = null;
    selectedPeriod.value = 'day';
    selectedApplicationId.value = null;
  };

  return {
    // State
    summary,
    applicationOverview,
    charts,
    dailyData,
    loading,
    error,
    selectedPeriod,
    selectedApplicationId,
    
    // Getters
    isLoading,
    errorMessage,
    currentPeriod,
    
    // Actions
    fetchSummary,
    fetchApplicationOverview,
    fetchCharts,
    fetchDailyData,
    fetchRealtime,
    exportMetrics,
    reset,
    clearError,
  };
});

